import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useTimer } from "use-timer";
import useSound from "use-sound";
import boopSfx from "sounds/enable-sound.mp3";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  align-content: center;
`;

const Divider = styled.span`
  margin: 0 5px;
`;

const Checkbox = styled.input.attrs({ type: "checkbox" })`
  border-radius: 5px;
  color: red;
`;

const Button = styled.button`
  margin: 5px 0;
  padding: 0.5rem 1rem;

  font-family: "Noto Sans KR", sans-serif;
  font-size: 1rem;
  font-weight: 400;
  text-align: center;
  text-decoration: none;

  display: inline-block;
  width: auto;

  border: none;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);

  background-color: #e67e22;

  cursor: pointer;

  transition: 0.5s;
  &:active,
  &:hover,
  &:focus {
    background: #d35400;
    outline: 0;
  }
  &:disabled {
    opacity: 0.5;
  }
`;

const TimerContainer = styled.div`
  font-family: Cambria, Cochin, Georgia, Times, "Times New Roman", serif;
  font-size: 20px;
  color: ${({ isCurrent }) => (isCurrent ? "#ecf0f1" : "#636e72")};
  opacity: ${({ isCurrent }) => (isCurrent ? 1 : 0.5)};
  margin: 16px;
  display: flex;
  justify-content: center;
`;

const TimingContainer = styled.div`
  margin: 16px;
  display: flex;
  justify-content: center;
`;

const Timing = styled.h1`
  opacity: ${({ isCurrent }) => (isCurrent ? 1 : 0.5)};
  color: #636e72;
  position: relative;
  font-weight: 800;
  font-size: 48px;
  text-align: center;

  &:after {
    content: "${({ timingSec }) => timingSec}";
  }

  &:before {
    transition: height 0s;
    content: "${({ timingSec }) => timingSec}";
    position: absolute;
    overflow: hidden;
    height: 0;
    color: #f1c40f;
  }

  ${({ isCurrent, timingSec }) =>
    isCurrent &&
    css`
      &:before {
        height: 48px;
        transition: ${`height ${timingSec}s cubic-bezier(.0, .35, .0, .25)`};
        };
      }
    `};
`;

const Timer = () => {
  const [playNextTimer] = useSound(boopSfx, { volume: 0.3 });
  const [isSoundOn, setIsSoundOn] = useState(false);
  const [isStart, setIsStart] = useState(false);
  const [timingSwitch, setTimingSwitch] = useState(-1);
  const [timing, setTiming] = useState([0, 0, 2, 2]);
  const [timerIndex, setTimerIndex] = useState(0);
  const [timingIndex, setTimingIndex] = useState(0);
  const config = {
    timerType: "DECREMENTAL",
    endTime: 0,
    onTimeUpdate: () => {
      if (isStart) {
        setTimingSwitch(timing[timingIndex]);
        setTimingIndex(() => {
          if (timerIndex === null) return -1;
          else return timingIndex === timing.length - 1 ? 0 : timingIndex + 1;
        });
      }
    },
    onTimeOver: () => {
      setTimerIndex((prev) => {
        const nextIdx = prev + 1;
        if (nextIdx === timerArray.length) {
          setTimingSwitch(-1);
          return null;
        }

        if (isSoundOn) playNextTimer();
        timerArray[nextIdx].timer.start();
        setTiming(timerArray[nextIdx].timing);
        return nextIdx;
      });
    },
  };

  const timerArray = [
    {
      timer: useTimer({ ...config, initialTime: 120 }),
      description: "1단계: 1유형, 2-0-2, 30회, 80%",
      timing: [0, 0, 2, 2],
    },
    {
      timer: useTimer({ ...config, initialTime: 180 }),
      description: "2단계: 1유형, 2-2-2, 30회, 80%",
      timing: [0, 0, 1, 1, 2, 2],
    },
    {
      timer: useTimer({ ...config, initialTime: 120 }),
      description: "3단계: 2유형, 1-2-1, 30회, 40%",
      timing: [0, 1, 1, 2],
    },
    {
      timer: useTimer({ ...config, initialTime: 180 }),
      description: "4단계: 2유형, 1-4-1, 30회, 40%",
      timing: [0, 1, 1, 1, 1, 2],
    },
    {
      timer: useTimer({ ...config, initialTime: 60 }),
      description: "5단계: 2유형, 1-0-1, 30회, 10%",
      timing: [0, 2],
    },
    {
      timer: useTimer({ ...config, initialTime: 240 }),
      description: "6단계: 2유형, 1-6-1, 30회, 10%",
      timing: [0, 1, 1, 1, 1, 1, 1, 2],
    },
  ];

  return (
    <Container>
      <label>
        <Checkbox onChange={(e) => setIsSoundOn(e.target.checked)} />
        단계 종료 시 효과음 재생
      </label>
      <Button
        disabled={isStart}
        onClick={() => {
          timerArray[timerIndex].timer.start();
          setIsStart(true);
        }}
      >
        Start
      </Button>
      <Button
        disabled={isStart}
        onClick={() => {
          setTiming(timerArray[4].timing);
          setTimerIndex(() => {
            timerArray[4].timer.start();
            setIsStart(true);

            return 4;
          });
        }}
      >
        5단계부터 시작
      </Button>
      {timerArray.map(({ timer: { time }, description }, index) => {
        const min = parseInt(time / 60);
        const sec = parseInt(time % 60);
        return (
          <TimerContainer key={index} isCurrent={timerIndex === index}>
            {`${min < 10 ? "0" : ""}${min}:`}
            {`${sec < 10 ? "0" : ""}${sec}`}
            <Divider>/</Divider>
            {description}
          </TimerContainer>
        );
      })}
      <TimerContainer isCurrent={timerIndex === null}>
        7단계: 흉식호흡, 반복, 10% 미만
      </TimerContainer>
      {/* <TimingTable /> */}
      <TimingContainer>
        <span>
          <h2>들숨</h2>
          <Timing
            isCurrent={timingSwitch === 0}
            timingSec={timing.filter((e) => e === 0).length}
          />
        </span>
        <Divider>-</Divider>
        <span>
          <h2>정지</h2>
          <Timing
            isCurrent={timingSwitch === 1}
            timingSec={timing.filter((e) => e === 1).length}
          />
        </span>
        <Divider>-</Divider>
        <span>
          <h2>날숨</h2>
          <Timing
            isCurrent={timingSwitch === 2}
            timingSec={timing.filter((e) => e === 2).length}
          />
        </span>
      </TimingContainer>
    </Container>
  );
};

export default Timer;
